import { SettingsOutlined } from '@mui/icons-material'
import { Box, Collapse, Divider, IconButton } from '@mui/material'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ModelController } from '../manager/ModelController'
import { AngleMeasurementTool } from './AngleMeasurementTool'
import { BoundingBoxTool } from './BoundingBoxTool'
import { DisplayModeTool } from './DisplayMode'
import { DistanceMeasurementTool } from './DistanceMeasurementTool'
import { ExplodeTool } from './ExplodeTool'
import { FitAllTool } from './FitAllTool'
import { SelectionMode } from './SelectionMode'
import { UnitSelectorTool } from './UnitSystemTool'

type Props = {
  bomItemPointer: BomItemPointer
  show3DOr2D: '3D' | '2D'
  viewController: ModelController
}

const useStyles = makeStyles({ name: 'model-viewer' })((theme) => ({
  toolbox: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    opacity: 0.5,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
}))

export const ViewerTools = (props: Props) => {
  const [HelpComponent, setHelpComponent] = useState<
    React.ReactElement<any> | undefined
  >(null)
  const { classes } = useStyles()

  const [configOpen, setConfigOpen] = useState(false)

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 10,
        width: '100%',
        // width: (theme) => theme.spacing(50),
      }}
    >
      <div className={classes.toolbox}>
        <FitAllTool viewManager={props.viewController} />
        <DistanceMeasurementTool
          controller={props.viewController}
          setHelpComponent={setHelpComponent}
        />
        <AngleMeasurementTool
          controller={props.viewController}
          setHelpComponent={setHelpComponent}
        />
        <BoundingBoxTool
          bomItemPointer={props.bomItemPointer}
          show3DOr2D={props.show3DOr2D}
          controller={props.viewController}
        />
        <IconButton
          size="small"
          onClick={() => setConfigOpen((prev) => !prev)}
          color="inherit"
          sx={{
            backgroundColor: configOpen ? 'primary.main' : 'transparent',
            color: configOpen ? 'white' : 'inherit',
            '&:hover': {
              backgroundColor: configOpen ? 'primary.main' : 'transparent',
              color: configOpen ? 'white' : 'inherit',
            },
          }}
        >
          <SettingsOutlined fontSize="small" />
        </IconButton>
      </div>

      <div style={{ width: '100%', textAlign: 'center' }}>
        <Collapse in={Boolean(HelpComponent)}>{HelpComponent}</Collapse>
      </div>
      <Collapse
        in={configOpen}
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: (theme) => theme.shape.borderRadius,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      >
        <Box sx={{ p: 2 }}>
          <LocalizedTypography
            translationKey="project:viewer-unit-of-measure"
            variant="caption"
            color="textSecondary"
          >
            unit
          </LocalizedTypography>
          <UnitSelectorTool
            controller={props.viewController}
            bomItemPointer={props.bomItemPointer}
          />
        </Box>
        <Box sx={{ p: 2, pt: 0 }}>
          <LocalizedTypography
            translationKey="project:viewer-selection-mode"
            variant="caption"
            color="textSecondary"
          >
            selection mode
          </LocalizedTypography>
          <SelectionMode viewManager={props.viewController} />
        </Box>
        {[BomItemType.assemblyInstance, BomItemType.assemblyType].includes(
          props.bomItemPointer.type,
        ) && (
          <>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <LocalizedTypography
                translationKey="project:viewer-explode-tool"
                variant="caption"
                color="textSecondary"
              >
                exploder
              </LocalizedTypography>
              <ExplodeTool viewController={props.viewController} />
            </Box>
          </>
        )}

        <Divider />
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <LocalizedTypography
            translationKey="project:viewer-display-mode"
            variant="caption"
            color="textSecondary"
            component="p"
          >
            display mode
          </LocalizedTypography>
          <DisplayModeTool viewManager={props.viewController} />
        </Box>
      </Collapse>
    </Box>
  )
}
